import { MeasurementMarkerDto, SnapshotMetaDto } from 'types/models/SessionsPageData';
import { formatDate } from 'utilities/DateUtils';
import { t } from 'utilities/TranslationUtils';
import { hoursSince } from './playerPerformanceUtils';

/** "N hours ago" — always in hours (never days), with a friendly label for a sub-hour age. */
function agoLabel(generatedAtUtc: string): string {
  const hours = hoursSince(generatedAtUtc);
  if (hours === 0) return t('playerPerformance.snapshotGeneratedRecent');
  return t(hours === 1 ? 'playerPerformance.snapshotGeneratedAgoOne' : 'playerPerformance.snapshotGeneratedAgoMany', { count: hours });
}

interface SnapshotInfoContentProps {
  meta: SnapshotMetaDto;
  focusedPlayerId?: string | null;
  focusedPlayerName?: string | null;
  measurements?: MeasurementMarkerDto[];
}

interface SessionTypeBreakdownRow {
  sessionType: string;
  sessionCount: number;
  measurementCount: number;
}

function prettifySessionTypeLabel(raw: string): string {
  return raw
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim();
}

function buildPlayerSessionTypeRows(measurements: MeasurementMarkerDto[], playerId: string): SessionTypeBreakdownRow[] {
  const grouped = new Map<string, { sessions: Set<string>; measurements: number }>();

  for (const measurement of measurements) {
    if (measurement.playerId !== playerId) continue;

    const sessionType = (measurement.kindRaw || measurement.kind || '').trim();
    if (!sessionType) continue;

    if (!grouped.has(sessionType)) {
      grouped.set(sessionType, { sessions: new Set<string>(), measurements: 0 });
    }

    const entry = grouped.get(sessionType)!;
    entry.measurements += 1;
    entry.sessions.add(measurement.sessionId);
  }

  return Array.from(grouped.entries())
    .map(([sessionType, data]) => ({
      sessionType,
      sessionCount: data.sessions.size,
      measurementCount: data.measurements,
    }))
    .sort((a, b) => b.measurementCount - a.measurementCount || a.sessionType.localeCompare(b.sessionType));
}

/**
 * Body of the header info popover: describes the loaded data snapshot — when it was generated
 * (with a relative "hours ago"), its org-wide counts, and the latest measurement time.
 * Presentational only; receives a fully-formed {@link SnapshotMetaDto}.
 */
const SnapshotInfoContent: React.FunctionComponent<SnapshotInfoContentProps> = ({
  meta,
  focusedPlayerId,
  focusedPlayerName,
  measurements = [],
}) => {
  const rows: { label: string; value: string }[] = [
    { label: t('playerPerformance.snapshotSessions'), value: meta.sessionCount.toLocaleString() },
    { label: t('playerPerformance.snapshotPlayers').toUpperCase(), value: meta.playerCount.toLocaleString() },
    { label: t('playerPerformance.snapshotTeams').toUpperCase(), value: meta.teamCount.toLocaleString() },
    { label: t('playerPerformance.snapshotClubs').toUpperCase(), value: meta.clubCount.toLocaleString() },
    { label: t('playerPerformance.snapshotMeasurements'), value: meta.measurementCount.toLocaleString() },
  ];
  const playerRows = focusedPlayerId ? buildPlayerSessionTypeRows(measurements, focusedPlayerId) : [];

  return (
    <div className='pp-snapshot-info' data-test='pp-snapshot-info'>
      <div className='pp-snapshot-info-title'>{t('playerPerformance.snapshotTitle')}</div>

      <div className='pp-snapshot-info-generated'>
        <span className='pp-snapshot-info-label'>{t('playerPerformance.snapshotGenerated')}</span>
        <span className='pp-snapshot-info-ago'>{agoLabel(meta.generatedAtUtc)}</span>
        <span className='pp-snapshot-info-date'>{formatDate(meta.generatedAtUtc, true)}</span>
      </div>

      <dl className='pp-snapshot-info-grid'>
        {rows.map((r) => (
          <div key={r.label} className='pp-snapshot-info-row'>
            <dt>{r.label}</dt>
            <dd>{r.value}</dd>
          </div>
        ))}
      </dl>

      <div className='pp-snapshot-info-latest'>
        <span className='pp-snapshot-info-label'>{t('playerPerformance.snapshotLatestMeasurement')}</span>
        <span className='pp-snapshot-info-date'>
          {meta.latestMeasurementUtc ? formatDate(meta.latestMeasurementUtc, true) : t('playerPerformance.na')}
        </span>
      </div>

      {focusedPlayerId && (
        <div className='pp-snapshot-info-player'>
          <div className='pp-snapshot-info-player-head'>
            <span className='pp-snapshot-info-label'>{t('playerPerformance.snapshotPlayerBreakdown')}</span>
            <span className='pp-snapshot-info-player-name'>{focusedPlayerName || t('playerPerformance.snapshotUnknownPlayer')}</span>
          </div>

          {playerRows.length > 0 ? (
            <div className='pp-snapshot-info-player-grid'>
              <div className='pp-snapshot-info-player-grid-head'>{t('playerPerformance.snapshotSessionType')}</div>
              <div className='pp-snapshot-info-player-grid-head pp-snapshot-info-player-grid-head--num'>{t('playerPerformance.snapshotSessions')}</div>
              <div className='pp-snapshot-info-player-grid-head pp-snapshot-info-player-grid-head--num'>{t('playerPerformance.snapshotMeasurements')}</div>

              {playerRows.map((row) => (
                <div key={row.sessionType} className='pp-snapshot-info-player-grid-row'>
                  <div className='pp-snapshot-info-player-cell'>{prettifySessionTypeLabel(row.sessionType)}</div>
                  <div className='pp-snapshot-info-player-cell pp-snapshot-info-player-cell--num'>{row.sessionCount.toLocaleString()}</div>
                  <div className='pp-snapshot-info-player-cell pp-snapshot-info-player-cell--num'>{row.measurementCount.toLocaleString()}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className='pp-snapshot-info-player-empty'>{t('playerPerformance.snapshotNoPlayerMeasurements')}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SnapshotInfoContent;
